import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InlineCopyLink from './InlineCopyLink';
import { useBrand } from '../../../brands/BrandProvider';
import {
  buildHiddenTicketUrl,
  formatTicketDate,
  formatTicketPrice,
  getTicketSaleStatus,
  TICKET_STATUS_COLOR,
  TICKET_STATUS_LABEL,
  type TicketFormSlice,
} from './paymentFormUtils';

interface TicketSummaryTableProps {
  tickets: TicketFormSlice[];
  expandedIndex: number | null;
  onEdit: (index: number) => void;
  hidePromotional?: boolean;
  slug?: string;
}

function visibilityChips(ticket: TicketFormSlice) {
  const chips: React.ReactNode[] = [];
  if (ticket.hiddenFromPublicPage) {
    chips.push(
      <Chip
        key="hidden"
        size="small"
        icon={<VisibilityOffOutlinedIcon />}
        label="Oculto"
        variant="outlined"
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    );
  }
  if (ticket.isRestricted) {
    chips.push(
      <Chip
        key="restricted"
        size="small"
        icon={<LockOutlinedIcon />}
        label="Restrito"
        variant="outlined"
        color="warning"
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    );
  }
  if (chips.length === 0) {
    chips.push(
      <Chip key="public" size="small" label="Público" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
    );
  }
  return <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>{chips}</Box>;
}

const TicketSummaryTable: React.FC<TicketSummaryTableProps> = ({
  tickets,
  expandedIndex,
  onEdit,
  hidePromotional,
  slug = '',
}) => {
  const brand = useBrand();
  const activeTickets = tickets
    .map((ticket, index) => ({ ticket, index }))
    .filter(({ ticket }) => ticket.isActive !== false)
    .filter(({ ticket }) => !hidePromotional || !ticket.hiddenFromPublicPage);

  if (activeTickets.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Nenhum ingresso ativo. Adicione um tipo de ingresso abaixo.
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ fontWeight: 700 }}>Ingresso</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Preço</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Vagas</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Período de vendas</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Visibilidade</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 280 }}>Link direto</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="center" width={56} />
          </TableRow>
        </TableHead>
        <TableBody>
          {activeTickets.map(({ ticket, index }) => {
            const status = getTicketSaleStatus(ticket);
            const isExpanded = expandedIndex === index;
            const hiddenLink = ticket.hiddenFromPublicPage
              ? buildHiddenTicketUrl(slug, ticket, brand.key)
              : '';

            return (
              <TableRow
                key={index}
                hover
                selected={isExpanded}
                sx={{ cursor: 'pointer' }}
                onClick={() => onEdit(index)}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {ticket.name?.trim() || `Ingresso ${index + 1}`}
                  </Typography>
                  {ticket.description && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 220, display: 'block' }}>
                      {ticket.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{formatTicketPrice(ticket.price)}</TableCell>
                <TableCell align="right">{ticket.quantity ?? '—'}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {formatTicketDate(ticket.startSaleDate)} → {formatTicketDate(ticket.endSaleDate)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={TICKET_STATUS_LABEL[status]}
                    color={TICKET_STATUS_COLOR[status]}
                  />
                </TableCell>
                <TableCell>{visibilityChips(ticket)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()} sx={{ py: 1.25 }}>
                  {ticket.hiddenFromPublicPage ? (
                    <InlineCopyLink link={hiddenLink} maxWidth={260} />
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Editar ingresso">
                    <IconButton size="small" onClick={() => onEdit(index)} aria-label="Editar ingresso">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TicketSummaryTable;
